import { useCallback, useRef, useState } from 'react';

import type { AccountSearchResultJson } from '../platform/api';
import { useFriends } from './store';
import styles from './friends.module.css';

export function FriendSearch(): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AccountSearchResultJson[]>([]);
  const [searching, setSearching] = useState(false);
  const searchUsers = useFriends((s) => s.searchUsers);
  const sendRequest = useFriends((s) => s.sendRequest);
  const debounce = useRef<ReturnType<typeof setTimeout>>();

  const handleChange = useCallback(
    (value: string) => {
      setQuery(value);
      clearTimeout(debounce.current);
      if (value.trim().length < 2) {
        setResults([]);
        return;
      }
      debounce.current = setTimeout(async () => {
        setSearching(true);
        const r = await searchUsers(value.trim());
        setResults(r);
        setSearching(false);
      }, 300);
    },
    [searchUsers],
  );

  const handleAdd = useCallback(
    async (id: number) => {
      await sendRequest(id);
      setResults((prev) => prev.filter((r) => r.account_id !== id));
    },
    [sendRequest],
  );

  return (
    <div className={styles.searchSection}>
      <input
        className={styles.searchInput}
        type="text"
        placeholder="Search by name or name#0000"
        value={query}
        onChange={(e) => handleChange(e.target.value)}
      />
      {searching && <div className={styles.searchHint}>Searching...</div>}
      {results.length > 0 && (
        <div className={styles.searchResults}>
          {results.map((r) => (
            <div key={r.account_id} className={styles.searchResult}>
              <span className={styles.searchResultName}>
                {r.display_name}
                <span className={styles.disc}>#{String(r.discriminator).padStart(4, '0')}</span>
              </span>
              <button
                className={styles.addBtn}
                onClick={() => void handleAdd(r.account_id)}
              >
                Add Friend
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
